import React from 'react';
import {
    CardHeader,
    CardContent,
    CardActions,
    Button,
    Box,
    Typography,
    useTheme,
    Alert,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Link,
    Paper,
    alpha,
} from '@mui/material';
import {
    Close as CloseIcon,
    CheckCircle as CheckCircleIcon,
    BarChart as BarChartIcon,
    WarningAmber as WarningIcon,
    FiberManualRecord as DotIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { DialogCard } from '../Cards/DialogCard';
import { setModal, updateConsent } from '../../Modules/Common/App';

export const CookieConsentModal = () => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const currentConsent = useSelector((state) => state.app?.consent);

    const handleAccept = () => {
        dispatch(updateConsent({
            recordedAt: new Date().toISOString(),
            status: 'accepted'
        }));
        dispatch(setModal({ open: false }));
    };

    const handleReject = () => {
        dispatch(updateConsent({
            recordedAt: new Date().toISOString(),
            status: 'rejected'
        }));
        dispatch(setModal({ open: false }));
    };

    const handleClose = () => {
        if (!currentConsent?.status) {
            handleReject();
        } else if (currentConsent?.status === 'rejected') {
            handleReject();
        } else {
            dispatch(setModal({ open: false }));
        }
    };

    return (
        <DialogCard>
            <CardHeader
                title={currentConsent?.status ? "Manage Your Preferences" : "Welcome! Let's Get Started"}
                action={
                    <Button
                        onClick={handleClose}
                        size="small"
                        sx={{ minWidth: 'auto', color: theme.palette.text.secondary }}
                    >
                        <CloseIcon fontSize="small" />
                    </Button>
                }
            />
            <CardContent sx={{ px: 2.5, py: 2 }}>
                <Typography
                    variant="body2"
                    sx={{
                        mb: 2,
                        lineHeight: 1.5,
                        color: theme.palette.text.primary,
                    }}
                >
                    {currentConsent?.status
                        ? 'To provide you with the best experience, we collect and process some data. This includes service data (needed to make the service work) and analytics data (to help us improve and maintain the service).'
                        : 'Thank you for using our free website analysis service! To provide you with the best experience, we collect and process some data. This includes service data (needed to make the service work) and analytics data (to help us improve and maintain the service).'}
                </Typography>

                <Paper
                    elevation={0}
                    sx={{
                        mb: 1.5,
                        p: 1.5,
                        borderRadius: 1.5,
                        bgcolor: alpha(theme.palette.success.main, 0.08),
                        border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Box
                            sx={{
                                p: 0.5,
                                borderRadius: 1,
                                bgcolor: alpha(theme.palette.success.main, 0.15),
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <CheckCircleIcon sx={{ color: theme.palette.success.main, fontSize: 18 }} />
                        </Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                            Service Data
                        </Typography>
                    </Box>
                    <List dense sx={{ py: 0, mb: 0.5, pl: 0.5 }}>
                        <ListItem sx={{ px: 0, py: 0.25 }}>
                            <ListItemIcon sx={{ minWidth: 28 }}>
                                <DotIcon sx={{ fontSize: 8, color: theme.palette.success.main }} />
                            </ListItemIcon>
                            <ListItemText
                                primary="Form submissions and service requests"
                                primaryTypographyProps={{
                                    variant: 'body2',
                                    color: 'text.secondary',
                                    sx: { lineHeight: 1.4 }
                                }}
                            />
                        </ListItem>
                        <ListItem sx={{ px: 0, py: 0.25 }}>
                            <ListItemIcon sx={{ minWidth: 28 }}>
                                <DotIcon sx={{ fontSize: 8, color: theme.palette.success.main }} />
                            </ListItemIcon>
                            <ListItemText
                                primary="Account information for service delivery"
                                primaryTypographyProps={{
                                    variant: 'body2',
                                    color: 'text.secondary',
                                    sx: { lineHeight: 1.4 }
                                }}
                            />
                        </ListItem>
                        <ListItem sx={{ px: 0, py: 0.25 }}>
                            <ListItemIcon sx={{ minWidth: 28 }}>
                                <DotIcon sx={{ fontSize: 8, color: theme.palette.success.main }} />
                            </ListItemIcon>
                            <ListItemText
                                primary="Session management for security"
                                primaryTypographyProps={{
                                    variant: 'body2',
                                    color: 'text.secondary',
                                    sx: { lineHeight: 1.4 }
                                }}
                            />
                        </ListItem>
                    </List>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                            fontStyle: 'italic',
                            display: 'block',
                            mt: 0.5,
                            pl: 3.5,
                        }}
                    >
                        This data is essential for the service to function properly
                    </Typography>
                </Paper>

                <Paper
                    elevation={0}
                    sx={{
                        mb: 1.5,
                        p: 1.5,
                        borderRadius: 1.5,
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Box
                            sx={{
                                p: 0.5,
                                borderRadius: 1,
                                bgcolor: alpha(theme.palette.primary.main, 0.15),
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <BarChartIcon sx={{ color: theme.palette.primary.main, fontSize: 18 }} />
                        </Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                            Analytics Data
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', fontSize: '0.7rem' }}>
                            (Part of free service)
                        </Typography>
                    </Box>
                    <List dense sx={{ py: 0, mb: 0.5, pl: 0.5 }}>
                        <ListItem sx={{ px: 0, py: 0.25 }}>
                            <ListItemIcon sx={{ minWidth: 28 }}>
                                <DotIcon sx={{ fontSize: 8, color: theme.palette.primary.main }} />
                            </ListItemIcon>
                            <ListItemText
                                primary="Google Analytics - Website usage statistics"
                                primaryTypographyProps={{
                                    variant: 'body2',
                                    color: 'text.secondary',
                                    sx: { lineHeight: 1.4 }
                                }}
                            />
                        </ListItem>
                        <ListItem sx={{ px: 0, py: 0.25 }}>
                            <ListItemIcon sx={{ minWidth: 28 }}>
                                <DotIcon sx={{ fontSize: 8, color: theme.palette.primary.main }} />
                            </ListItemIcon>
                            <ListItemText
                                primary="Microsoft Clarity - Session recordings to improve UX"
                                primaryTypographyProps={{
                                    variant: 'body2',
                                    color: 'text.secondary',
                                    sx: { lineHeight: 1.4 }
                                }}
                            />
                        </ListItem>
                    </List>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                            fontStyle: 'italic',
                            display: 'block',
                            mt: 0.5,
                            pl: 3.5,
                        }}
                    >
                        This helps us understand how to make the service better for everyone
                    </Typography>
                </Paper>

                <Alert
                    severity={!currentConsent?.status ? 'info' : (currentConsent?.status === 'accepted' ? 'success' : 'info')}
                    icon={<WarningIcon />}
                    sx={{
                        mt: 1.5,
                        borderRadius: 1.5,
                        py: 1,
                        '& .MuiAlert-icon': {
                            fontSize: 20
                        },
                        '& .MuiAlert-message': {
                            py: 0
                        }
                    }}
                >
                    <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.4 }}>
                        {!currentConsent?.status
                            ? 'By clicking "I Agree", you consent to data collection as described above. If you decline, you can still use our free service, but analytics will be disabled.'
                            : currentConsent?.status === 'accepted'
                                ? `✓ Analytics is currently enabled${currentConsent?.recordedAt ? ` (accepted on ${new Date(currentConsent.recordedAt).toLocaleDateString()})` : ''}. You can withdraw consent using the button below.`
                                : `Analytics is currently disabled${currentConsent?.recordedAt ? ` (rejected on ${new Date(currentConsent.recordedAt).toLocaleDateString()})` : ''}. You can enable it using the button below. Changes will take effect immediately.`}
                    </Typography>
                </Alert>

            </CardContent>
            <CardActions sx={{ justifyContent: 'space-between', px: 3, pb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {!currentConsent?.status ? (
                        <>
                            <Button
                                onClick={handleReject}
                                variant="outlined"
                                color="inherit"
                            >
                                Decline
                            </Button>
                            <Typography variant="body2" color="text.secondary" component="span" sx={{ mx: 0.5 }}>
                                |
                            </Typography>
                        </>
                    ) : currentConsent?.status === 'rejected' ? (
                        <Button
                            onClick={handleClose}
                            variant="outlined"
                            color="inherit"
                        >
                            Close
                        </Button>
                    ) : (
                        <Button
                            onClick={handleReject}
                            variant="outlined"
                            color="inherit"
                        >
                            Withdraw Consent
                        </Button>
                    )}
                    <Link
                        href="https://dgtia.com/privacy-policy/"
                        target="_blank"
                        rel="noopener noreferrer"
                        color="primary"
                        underline="hover"
                        sx={{ fontWeight: 500, fontSize: '0.875rem' }}
                    >
                        Privacy Policy
                    </Link>
                    <Typography component="span" variant="body2" color="text.secondary">
                        |
                    </Typography>
                    <Link
                        href="https://dgtia.com/terms-of-service/"
                        target="_blank"
                        rel="noopener noreferrer"
                        color="primary"
                        underline="hover"
                        sx={{ fontWeight: 500, fontSize: '0.875rem' }}
                    >
                        Terms of Service
                    </Link>
                </Box>
                {!currentConsent?.status ? (
                    <Button
                        onClick={handleAccept}
                        variant="contained"
                        color="primary"
                    >
                        I Agree, Let's Continue
                    </Button>
                ) : currentConsent?.status === 'rejected' ? (
                    <Button
                        onClick={handleAccept}
                        variant="contained"
                        color="primary"
                    >
                        Accept
                    </Button>
                ) : (
                    <Button
                        onClick={handleClose}
                        variant="contained"
                        color="primary"
                    >
                        Close
                    </Button>
                )}
            </CardActions>
        </DialogCard>
    );
};

